/**
 * Forgot Password Form Component
 * Implements Amplify forgotPassword flow:
 * 1. Send reset code
 * 2. Confirm code + set new password
 */

import React, { useMemo, useState } from 'react';
import { Mail, AlertCircle, Check, ArrowLeft, Hash, Lock } from 'lucide-react';
import { usePasswordReset } from '../../auth/authHooks';
import './ForgotPasswordForm.css';

type ResetStep = 'request' | 'confirm' | 'success';

interface ForgotPasswordFormProps {
    onSwitchToLogin?: () => void;
}

const PASSWORD_REQUIREMENTS = [
    'Contains at least 1 number',
    'Contains at least 1 special character',
    'Contains at least 1 uppercase letter',
    'Contains at least 1 lowercase letter',
];

const passwordChecks = (value: string) => ({
    hasNumber: /\d/.test(value),
    hasSpecial: /[^A-Za-z0-9]/.test(value),
    hasUppercase: /[A-Z]/.test(value),
    hasLowercase: /[a-z]/.test(value),
});

const meetsPasswordRequirements = (value: string) => {
    const checks = passwordChecks(value);
    return Object.values(checks).every(Boolean);
};

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onSwitchToLogin }) => {
    const [step, setStep] = useState<ResetStep>('request');
    const [formData, setFormData] = useState({
        email: '',
        code: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [statusMessage, setStatusMessage] = useState('');
    const [serverError, setServerError] = useState('');

    const { forgotPassword, confirmForgotPassword, isLoading, error } = usePasswordReset();

    const passwordState = useMemo(() => passwordChecks(formData.newPassword), [formData.newPassword]);

    const validateEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSendCode = async (e: React.FormEvent) => {
        e.preventDefault();
        const { email } = formData;
        const errors: Record<string, string> = {};

        if (!email.trim()) {
            errors.email = 'Email is required';
        } else if (!validateEmail(email)) {
            errors.email = 'Please enter a valid email address';
        }

        if (Object.keys(errors).length) {
            setValidationErrors(errors);
            return;
        }

        setStatusMessage('');
        setServerError('');
        const result = await forgotPassword(email.trim());
        if (result.success) {
            setStep('confirm');
            setStatusMessage(`Verification code sent to ${email.trim()}. Please check your email or SMS.`);
            setServerError('');
        } else {
            setServerError(result.error?.message || 'Failed to send reset code.');
        }
    };

    const handleConfirmReset = async (e: React.FormEvent) => {
        e.preventDefault();
        const { email, code, newPassword, confirmPassword } = formData;
        const errors: Record<string, string> = {};

        if (!code.trim()) {
            errors.code = 'Verification code is required';
        }

        if (!newPassword) {
            errors.newPassword = 'New password is required';
        } else if (newPassword.length < 8) {
            errors.newPassword = 'Password must be at least 8 characters long';
        } else if (!meetsPasswordRequirements(newPassword)) {
            errors.newPassword = 'Password must meet all requirements listed below';
        }

        if (!confirmPassword) {
            errors.confirmPassword = 'Please confirm your password';
        } else if (newPassword !== confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        if (Object.keys(errors).length) {
            setValidationErrors(errors);
            return;
        }

        setStatusMessage('');
        setServerError('');
        const result = await confirmForgotPassword(email.trim(), code.trim(), newPassword);
        if (result.success) {
            setStep('success');
            setStatusMessage('Password has been updated successfully.');
            setServerError('');
        } else {
            setServerError(result.error?.message || 'Failed to reset password.');
        }
    };

    const handleResendCode = async () => {
        if (!formData.email) return;
        setServerError('');
        const result = await forgotPassword(formData.email.trim());
        if (result.success) {
            setStatusMessage('Verification code resent successfully.');
            setServerError('');
        } else {
            setServerError(result.error?.message || 'Failed to resend code.');
        }
    };

    const activeError = serverError || error?.message || '';

    const renderRequestStep = () => (
        <form onSubmit={handleSendCode} className="form">
            <div className="form-group">
                <label htmlFor="email" className="form-label">
                    Email Address
                </label>
                <div className="input-container">
                    <Mail className="input-icon" size={20} />
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`form-input ${validationErrors.email ? 'error' : ''}`}
                        placeholder="Enter your email address"
                        disabled={isLoading}
                    />
                </div>
                {validationErrors.email && (
                    <span className="form-error-message">
                        <AlertCircle size={16} />
                        {validationErrors.email}
                    </span>
                )}
            </div>

            {activeError && (
                <div className="error-banner">
                    <AlertCircle size={20} />
                    <span>{activeError}</span>
                </div>
            )}

            {statusMessage && !activeError && <p className="status-message">{statusMessage}</p>}

            <button type="submit" className="btn btn-primary btn-full" disabled={isLoading}>
                {isLoading ? (
                    <>
                        <div className="spinner" />
                        Sending Reset Code...
                    </>
                ) : (
                    'Send Reset Code'
                )}
            </button>
        </form>
    );

    const renderConfirmStep = () => (
        <form onSubmit={handleConfirmReset} className="form">
            <div className="form-group">
                <label htmlFor="code" className="form-label">
                    Verification Code
                </label>
                <div className="input-container has-action">
                    <Hash className="input-icon" size={20} />
                    <input
                        type="text"
                        id="code"
                        name="code"
                        value={formData.code}
                        onChange={handleInputChange}
                        className={`form-input code-input ${validationErrors.code ? 'error' : ''}`}
                        placeholder="Enter the 6-digit code"
                        disabled={isLoading}
                    />
                    <button
                        type="button"
                        className="resend-inline"
                        onClick={handleResendCode}
                        disabled={isLoading}
                    >
                        Resend
                    </button>
                </div>
                {validationErrors.code && (
                    <span className="form-error-message">
                        <AlertCircle size={16} />
                        {validationErrors.code}
                    </span>
                )}
            </div>

            <div className="form-group">
                <label htmlFor="newPassword" className="form-label">
                    New Password
                </label>
                <div className="input-container">
                    <Lock className="input-icon" size={20} />
                    <input
                        type="password"
                        id="newPassword"
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        className={`form-input with-icon ${validationErrors.newPassword ? 'error' : ''}`}
                        placeholder="Enter a new password"
                        disabled={isLoading}
                    />
                </div>
                {validationErrors.newPassword && (
                    <span className="form-error-message">
                        <AlertCircle size={16} />
                        {validationErrors.newPassword}
                    </span>
                )}

                <div className="password-requirements">
                    <span className="requirements-title">Password requirements</span>
                    <ul>
                        {PASSWORD_REQUIREMENTS.map((requirement, index) => {
                            const keys = ['hasNumber', 'hasSpecial', 'hasUppercase', 'hasLowercase'] as const;
                            const met = passwordState[keys[index]];
                            return (
                                <li key={requirement} className={`requirement-item ${met ? 'met' : ''}`}>
                                    {met ? <Check size={16} /> : <span className="bullet" />}
                                    <span>{requirement}</span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>

            <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">
                    Confirm New Password
                </label>
                <div className="input-container">
                    <Lock className="input-icon" size={20} />
                    <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className={`form-input with-icon ${validationErrors.confirmPassword ? 'error' : ''}`}
                        placeholder="Re-enter your new password"
                        disabled={isLoading}
                    />
                </div>
                {validationErrors.confirmPassword && (
                    <span className="form-error-message">
                        <AlertCircle size={16} />
                        {validationErrors.confirmPassword}
                    </span>
                )}
            </div>

            {activeError && (
                <div className="error-banner">
                    <AlertCircle size={20} />
                    <span>{activeError}</span>
                </div>
            )}

            <button type="submit" className="btn btn-primary btn-full" disabled={isLoading}>
                {isLoading ? (
                    <>
                        <div className="spinner" />
                        Resetting Password...
                    </>
                ) : (
                    'Confirm New Password'
                )}
            </button>
        </form>
    );

    const renderSuccessStep = () => (
        <div className="success-state">
            <div className="success-icon">
                <Check size={48} />
            </div>
            <h2>Password Reset Successful</h2>
            <p>Your password has been updated. You can now sign in with your new credentials.</p>
            <div className="success-actions">
                <button type="button" className="btn btn-primary" onClick={onSwitchToLogin}>
                    Back to Sign In
                </button>
            </div>
        </div>
    );

    const getTitle = () => {
        switch (step) {
            case 'confirm':
                return 'Verify Reset Code';
            case 'success':
                return 'All Set!';
            default:
                return 'Reset Password';
        }
    };

    const getSubtitle = () => {
        switch (step) {
            case 'confirm':
                return 'Verification code sent. Please check your email. Enter the verification code and set a new password';
            case 'success':
                return 'Your password has been updated';
            default:
                return 'Enter your email to receive a reset code';
        }
    };

    return (
        <div className="forgot-password-form">
            <div className="form-header">
                {step !== 'success' && (
                    <button type="button" className="back-button" onClick={onSwitchToLogin} disabled={isLoading}>
                        <ArrowLeft size={20} />
                        Back to Sign In
                    </button>
                )}
                <h2>{getTitle()}</h2>
                <p>{getSubtitle()}</p>
            </div>

            {step === 'request' && renderRequestStep()}
            {step === 'confirm' && renderConfirmStep()}
            {step === 'success' && renderSuccessStep()}
        </div>
    );
};

export default ForgotPasswordForm;
